/**
 * Post-op after-care link - what the patient page keeps of a saved form. Pure.
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE with NAMED exports only - cypress/node-tests/post-op-editor.test.js executes it.
 *
 * The page renders the form NORMALLY (FormRenderer's `readOnly` is NOT "display only": it is the submitted-answers view -
 * ControlView swaps every field for its saved value and prints a label for every control, so text blocks and wiki content
 * vanish and a list of control names is all that is left). What makes the page harmless instead is that it has no submit
 * (`showSubmit:false`), no patNum (no draft), and a ViewForm that ignores `submit` events and draft saves in post-op mode.
 *
 * Two more things are taken out of the saved config here:
 *   - the file uploader, which pushes the chosen file to S3 the moment it is picked (a post-op form has no business asking
 *     a patient for a file);
 *   - section break points (`breakAfter`). They split a form into Continue / Back parts, and with `showSubmit:false` the last
 *     part would have no Back button; instructions are one scrolling page.
 */

/** Control types removed from a post-op page because picking something has an effect on its own. */
export const POST_OP_DROPPED_CONTROL_TYPES = Object.freeze(['fileUploader']);

/**
 * @param {object} config a sanitized saved-form config ({ formConfig, sections, rows, controls })
 * @returns {object} a copy without the dropped controls (from `controls` AND from every section's control list) and with every
 *   section's `breakAfter` off; the input is not mutated
 */
export function stripSideEffectControls(config) {
  if (!config || typeof config !== 'object') return config;
  const controls = config.controls && typeof config.controls === 'object' ? config.controls : {};
  const dropped = {};
  const keptControls = {};
  Object.keys(controls).forEach((id) => {
    const c = controls[id];
    if (c && POST_OP_DROPPED_CONTROL_TYPES.indexOf(c.type) !== -1) dropped[id] = true;
    else keptControls[id] = c;
  });
  const sections = {};
  Object.keys(config.sections || {}).forEach((sid) => {
    const section = config.sections[sid];
    if (!section || typeof section !== 'object') {
      sections[sid] = section;
      return;
    }
    const next = Object.assign({}, section, { breakAfter: false });
    if (Array.isArray(section.controls)) next.controls = section.controls.filter((id) => !dropped[id]);
    sections[sid] = next;
  });
  return Object.assign({}, config, { controls: keptControls, sections });
}
